import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FOUNDING, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Check, Zap, Wrench } from "lucide-react";
import { toast } from "sonner";

function SpotsCounter({ spots }) {
  if (!spots) return null;
  const full = spots.remaining <= 0;
  const pct = Math.min(100, (spots.taken / spots.total) * 100);
  return (
    <div className="mt-5" data-testid="founding-spots-counter">
      <div className="flex justify-between text-xs font-mono uppercase tracking-wider">
        <span className={full ? "text-red-300" : "text-amber-300"} data-testid="founding-spots-remaining">{full ? "Founding spots full" : `Only ${spots.remaining} of ${spots.total} founding spots left`}</span>
        <span className="text-slate-400">{spots.taken}/{spots.total} claimed</span>
      </div>
      <div className="h-2 rounded-full bg-white/10 mt-2 overflow-hidden"><div className="h-full bg-amber-400 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function PlanCard({ plan, icon: Icon, audience, note, dark, loading, onBuy, testid, extra, disabled, cta }) {
  return (
    <div className={`rounded-3xl p-8 lg:p-10 relative overflow-hidden ${dark ? "bg-[color:var(--navy)] text-white" : "card-soft"}`} data-testid={testid}>
      {dark && <div className="hero-grid-bg absolute inset-0 opacity-20" />}
      <div className="relative">
        <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider badge-pest px-3 py-1.5 rounded-full"><Icon className="w-3.5 h-3.5" /> {audience}</span>
        <h2 className={`font-display text-3xl font-bold mt-4 ${dark ? "" : "text-[color:var(--navy)]"}`}>{plan.name}</h2>
        <div className="mt-4 flex items-end gap-2">
          <span className={`font-display text-5xl font-extrabold ${dark ? "" : "text-[color:var(--navy)]"}`} data-testid={`${testid}-price`}>${plan.price}</span>
          <span className={`mb-1 ${dark ? "text-slate-400" : "text-[color:var(--ink-3)]"}`}>/mo after 60 days free</span>
        </div>
        <p className={`text-sm mt-2 ${dark ? "text-slate-300" : "text-[color:var(--ink-2)]"}`}>{note}</p>
        {extra}
        <ul className="mt-6 space-y-2">
          {plan.features.map((f) => (
            <li key={f} className={`flex items-center gap-2 text-sm ${dark ? "text-slate-200" : "text-[color:var(--ink-2)]"}`}><Check className="w-4 h-4 text-emerald-500 shrink-0" /> {f}</li>
          ))}
        </ul>
        <Button onClick={() => onBuy(plan.key)} disabled={loading || disabled} data-testid={`${testid}-subscribe-button`}
          className={`rounded-full w-full mt-8 font-semibold ${dark ? "btn-amber" : "btn-primary"}`}>{loading ? "Redirecting..." : cta || "Start 60-day free trial"}</Button>
        <p className={`text-xs mt-3 text-center ${dark ? "text-slate-500" : "text-[color:var(--ink-3)]"}`}>Cancel anytime. No contract.</p>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, business } = useAuth();
  const nav = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);
  const [spots, setSpots] = useState(null);
  useEffect(() => { api.get("/stats/founding").then(({ data }) => setSpots(data)).catch(() => {}); }, []);
  const full = spots && spots.remaining <= 0;

  const checkout = async (lookup_key) => {
    if (!user) { startGoogleLogin(); return; }
    if (!business) { toast.info("Set up your business profile first."); nav("/dashboard"); return; }
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  return (
    <div className="container-x py-14">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <span className="text-xs font-mono uppercase tracking-wider text-[color:var(--emerald)]">Membership</span>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--navy)] mt-2">Simple pricing. 60 days free.</h1>
        <p className="text-[color:var(--ink-2)] mt-4">One low monthly plan for home-service pros and inspectors. Try it free for 60 days, and your profile goes live as soon as you start.</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <PlanCard plan={FOUNDING} icon={Wrench} audience="For service vendors" dark testid="pricing-vendor-plan"
          note="Inspectors of every kind, pest, roofing, HVAC, contractors & appraisers. Your $15 rate is locked for 12 months."
          loading={loadingKey === FOUNDING.key} onBuy={checkout} extra={<SpotsCounter spots={spots} />}
          disabled={full} cta={full ? "Founding spots full" : null} />
      </div>

      <div className="max-w-5xl mx-auto mt-12 card-soft p-8 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="credit-pack-1">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider badge-pest px-3 py-1.5 rounded-full"><Zap className="w-3.5 h-3.5" /> Pay per lead</span>
          <h3 className="font-display text-2xl font-bold text-[color:var(--navy)] mt-3">Need one more lead? $10 each.</h3>
          <p className="text-sm text-[color:var(--ink-2)] mt-1">Vendors can buy single lead credits anytime. Credits never expire.</p>
        </div>
        <Button disabled={loadingKey === "credits_1"} onClick={() => checkout("credits_1")} data-testid="buy-credits-1-button"
          className="btn-amber rounded-full font-semibold px-8">{loadingKey === "credits_1" ? "Redirecting..." : "Buy 1 lead — $10"}</Button>
      </div>
    </div>
  );
}
